import type { Coupon, Flags } from 'shared-types/Coupon'
import { OrderType } from 'shared-types/Coupon'

export const EXTERNAL_ORDER_TYPES: readonly OrderType[] = [OrderType.IN_STORE, OrderType.DRIVETHROUGH]
export const INTERNAL_ORDER_TYPES: readonly OrderType[] = Object.values(OrderType).filter((orderType) => !EXTERNAL_ORDER_TYPES.includes(orderType))

export type CouponFlags = keyof Flags
/**
 * Virtual fields that add shortcuts to access flag values
 * @example
 * // Assume we have a flag called 'wallet':
 * flags: {
 *   wallet?: {
 *     value: boolean
 *   }
 * }
 *
 * // We can access it in two ways:
 * coupon.flags.wallet?.value
 *
 * // or, using 'virtuals',
 * coupon.wallet // under the hood, calls coupon.flags.wallet?.value
 */
export type FlagShortcuts = Partial<{ [Key in CouponFlags]: Flags[Key]['value'] }>
// Make sure to update this list after adding a new flag
export const couponFlags: readonly (keyof Flags)[] = ['applied', 'requireLogin', 'wallet', 'active', 'showInMyCoupons']

type AllKeys<T> = T extends any ? keyof T : never
type ExtractFieldType<T, K extends string | number | symbol> = T extends { [P in K]: infer R } | { [P in K]?: infer R } ? R : never

export type CouponUnion = Pick<Coupon, keyof Coupon> & { [P in Exclude<AllKeys<Coupon>, keyof Coupon>]?: ExtractFieldType<Coupon, P> }

/**
 * A coupon whose title and description are localized,
 * i.e. are in a specific language instead of containing all locale variants
 */
export interface LocalizedCoupon extends Omit<CouponUnion, 'title' | 'description'> {
	title: string
	description: string
	formattedExpiration?: string
}
